// The sampled frames as a strip of thumbnails, with a dot where the
// reviewer noted something. `shown` lists the frame indexes to include
// (all by default) so a filter can narrow the strip. One tab stop: arrow
// keys, Home and End move between the frames shown. Frame URLs are signed
// and expire, so they are never cached; the result window refetches them
// when it opens.
import React, { useRef } from 'react';
import { TONE } from './qcFormat';
import { useDialogFocus } from './ui';

const KEYS = { ArrowLeft: -1, ArrowRight: 1 };
const TONE_WORD = { bad: 'problem', flag: 'flagged', warn: 'check', good: 'good' };

export function Lightbox({ frame, notes = [], onClose }) {
    const { ref, onKeyDown } = useDialogFocus(onClose);
    return (
        <div role="dialog" aria-modal="true" aria-label={`Frame ${frame.ts}`} onClick={onClose} onKeyDown={onKeyDown}
            style={{ position: 'fixed', inset: 0, background: 'rgba(8,11,26,0.9)', zIndex: 3100, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, gap: 12 }}>
            <img ref={ref} tabIndex={-1} src={frame.url} alt={`Frame at ${frame.ts}`} style={{ maxHeight: '80vh', maxWidth: '90vw', borderRadius: 10, outline: 'none' }} />
            <div style={{ color: '#fff', fontSize: 14, textAlign: 'center', maxWidth: 640 }}>
                <b>{frame.ts}</b>{notes.length > 0 && ` · ${notes.join(' · ')}`}
            </div>
        </div>
    );
}

export default function Filmstrip({ frames, shown = frames.map((_, i) => i), selectedIndex, frameTones, size, onSelect }) {
    const strip = useRef(null);
    const pos = shown.indexOf(selectedIndex);

    const move = (to) => {
        if (!shown.length) return;
        const next = Math.min(shown.length - 1, Math.max(0, to));
        onSelect(shown[next]);
        const button = strip.current?.querySelectorAll('button')[next];
        if (button) button.focus();
    };

    const onKeyDown = (e) => {
        if (KEYS[e.key]) move(pos + KEYS[e.key]);
        else if (e.key === 'Home') move(0);
        else if (e.key === 'End') move(shown.length - 1);
        else return;
        e.preventDefault();
    };

    return (
        <div ref={strip} role="toolbar" aria-label="Frames" onKeyDown={onKeyDown}
            style={{ display: 'flex', gap: 5, overflowX: 'auto', padding: '4px 2px 6px' }}>
            {shown.map((i, k) => {
                const f = frames[i];
                const selected = i === selectedIndex;
                const tone = frameTones.get(i);
                return (
                    <button key={`${f.ts}-${i}`} type="button" className="vqc-thumb"
                        tabIndex={selected || (pos < 0 && k === 0) ? 0 : -1} aria-current={selected ? 'true' : undefined}
                        aria-label={`Frame ${f.ts}${tone ? `, ${TONE_WORD[tone]}` : ''}`}
                        onClick={() => onSelect(i)}
                        style={{ position: 'relative', flex: '0 0 auto', width: size.width, height: size.height, padding: 0, borderRadius: 6, overflow: 'hidden', cursor: 'pointer', background: '#0b1029', border: `2px solid ${selected ? '#febd1c' : 'transparent'}` }}>
                        <img src={f.url} alt="" loading="lazy" style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }} />
                        {tone && <span style={{ position: 'absolute', left: '50%', bottom: 3, width: 7, height: 7, marginLeft: -3.5, borderRadius: 7, background: TONE[tone].color, boxShadow: '0 0 0 1.5px #fff' }} />}
                    </button>
                );
            })}
        </div>
    );
}
